import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import GridBackground from "./components/GridBackground";
import Navbar from "./components/Navbar";
import Hero from "./components/sections/Hero";
import About from "./components/sections/About";
import Experience from "./components/sections/Experience";
import Projects from "./components/sections/Projects";
import Contact from "./components/sections/Contact";
import Footer from "./components/Footer";

const SITE_URL = "https://www.nfatihakkin.com";

const pageMeta = {
  "/": {
    title: "Nurettin Fatih AKKIN | Software Engineer & .NET Developer",
    description:
      "Backend engineer building the ERP and real-time MES systems that keep factory floors running. .NET, C#, clean architecture."
  },
  "/about": {
    title: "About | Nurettin Fatih AKKIN",
    description:
      "Backend-focused software engineer specializing in ERP and production systems on the .NET stack."
  },
  "/experience": {
    title: "Experience | Nurettin Fatih AKKIN",
    description:
      "Professional experience in ERP, MES, real-time production systems, and .NET backend architecture."
  },
  "/projects": {
    title: "Projects | Nurettin Fatih AKKIN",
    description:
      "Live GitHub repositories: .NET, manufacturing systems, production scheduling, and system integration."
  },
  "/contact": {
    title: "Contact | Nurettin Fatih AKKIN",
    description:
      "Get in touch with Nurettin Fatih AKKIN for backend engineering, ERP/MES work, and technical collaboration."
  }
};

const setMetaTag = (selector, attr, value) => {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
};

const ScrollToTopAndMeta = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });

    const raw = location.pathname;
    const path = raw !== "/" && raw.endsWith("/") ? raw.slice(0, -1) : raw;
    const meta = pageMeta[path] || pageMeta["/"];
    const canonical = `${SITE_URL}${path === "/" ? "/" : path}`;

    document.title = meta.title;
    setMetaTag('meta[name="description"]', "content", meta.description);
    setMetaTag('link[rel="canonical"]', "href", canonical);
    setMetaTag('meta[property="og:title"]', "content", meta.title);
    setMetaTag('meta[property="og:description"]', "content", meta.description);
    setMetaTag('meta[property="og:url"]', "content", canonical);
  }, [location.pathname]);

  return null;
};

const SiteShell = ({ children }) => (
  <div className="relative min-h-screen">
    <GridBackground />
    <Navbar />
    <main className="relative z-10">{children}</main>
    <Footer />
  </div>
);

const HomePage = () => (
  <SiteShell>
    <Hero />
    <About />
    <Experience />
    <Projects />
    <Contact />
  </SiteShell>
);

const AboutPage = () => (
  <SiteShell>
    <h1 className="sr-only">About Nurettin Fatih AKKIN</h1>
    <About />
  </SiteShell>
);

const ExperiencePage = () => (
  <SiteShell>
    <h1 className="sr-only">Professional Experience of Nurettin Fatih AKKIN</h1>
    <Experience />
  </SiteShell>
);

const ProjectsPage = () => (
  <SiteShell>
    <h1 className="sr-only">Projects by Nurettin Fatih AKKIN</h1>
    <Projects />
  </SiteShell>
);

const ContactPage = () => (
  <SiteShell>
    <h1 className="sr-only">Contact Nurettin Fatih AKKIN</h1>
    <Contact />
  </SiteShell>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTopAndMeta />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/experience" element={<ExperiencePage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Routes>
        <Toaster
          position="bottom-right"
          theme="dark"
          toastOptions={{
            style: {
              background: "#10141c",
              border: "1px solid #212a38",
              color: "#e7eaf0",
              fontFamily: "Inter, sans-serif"
            }
          }}
        />
      </BrowserRouter>
    </div>
  );
}

export default App;
